import { useState, useEffect, useCallback, useRef, Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { erfuelltRecht, geldText } from '@symphenia/shared-types'
import { fmtDateLong, fmtTimeUTC } from '@/lib/format-date'
import { useHoursFormat, formatHoursWithUnit } from '@/lib/hours-format'
import { translateApiErrorKey } from '@/lib/api-error'
import type { TFunction } from 'i18next'
import { Settings, Check, ArrowUp, ArrowDown, ArrowUpDown, AlertTriangle, ShieldCheck, Pencil, ClipboardCheck, BadgeCheck, Trash2, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { ExportButton } from '@/components/ui/ExportButton'
import { betreffMitZeitraum } from '@/lib/mail/betreff'
import { SearchSelect } from '../components/SearchSelect'
import { useAuthStore } from '@/lib/auth.store'
import { timeEntriesApi, timeConfigApi, picklistsApi } from '../api/time-tracking.api'
import {
  resolveEntryPositionName,
  resolveEntryStepName,
  usesRefLayer,
  useBookingDataSource,
} from '../hooks/useBookingDataSource'
import { usersApi } from '@/lib/users/users.api'
import type { User } from '@/lib/users/user.types'
import { useSearchParams } from 'react-router-dom'
import { FilterBar, filterAusAdresse } from '@/components/list/FilterBar'
import { HelpPopover } from '@/components/ui/HelpPopover'
import { HILFE_VERWEISE } from '@/lib/hilfe'
import { loadUiSetting, markUiSettingApplied, saveUiSetting, saveUiSettingIfChanged } from '@/lib/ui-settings'
import { LayoutDefaultActions } from '@/components/ui/LayoutDefaultActions'
import { useTerminology, type Terms } from '@/lib/useTerminology'
import type {
  TimeEntry,
  ActivityType,
  TimeEntryStatus,
  TenantTimeConfig,
  UpdateTimeEntryPayload,
  DataSourceMode,
  StandardFieldDefinition,
  StandardSelectionItem,
} from '../types/time-tracking.types';
import type { FilterFieldDef, ActiveFilter, ColumnFieldDef, ColumnLayout, ColumnSortConfig } from '@/components/list/list.types';
import { ohneFehlendeRechte } from '@/components/list/list.types';
import { pageWide } from '@/lib/form-styles'
import { bestaetige } from '@/lib/dialoge'

// ─── Column layout persistence ────────────────────────────────────────────────

const COLUMN_LAYOUT_KEY = 'columnbar_layout_summary'
const PAGE_SIZE_KEY = 'pagesize_summary'

const DEFAULT_COLUMN_KEYS = ['employee', 'project', 'activityType', 'start', 'end', 'duration', 'status']

/**
 * Vorbelegung der FilterBar: Zeitraum + Mitarbeiter (Auftraggeber, 6.9.2026).
 * 'userId' existiert nur für Manager — für alle anderen fällt es beim Rendern
 * still weg, übrig bleiben Von/Bis. Das ist so gewollt.
 */
const DEFAULT_FILTER_FIELD_KEYS = ['dateFrom', 'dateTo', 'userId']

/** Column keys that map to real Prisma sort fields (server-side sorting supported) */
const SERVER_SORTABLE_KEYS = new Set(['date', 'start', 'end', 'duration', 'break', 'mode', 'status', 'project', 'activityType', 'employee', 'vorname', 'nachname'])

// AP-23: Gelesen wird synchron aus dem Cache, geschrieben wird Cache sofort +
// API debounced. Keine Filterung nach verfügbaren Spalten hier –
// shownColumns filtert beim Rendern (unknown keys → undefined → filter(Boolean)).
// Ein gespeicherter Spaltenschlüssel ist keine Berechtigungsentscheidung —
// sichtbar wird die Spalte weiterhin nur mit ihrem Feldrecht (AP-30-F).
function loadColumnLayout(): ColumnLayout {
  const parsed = loadUiSetting<ColumnLayout | null>(COLUMN_LAYOUT_KEY, null)
  if (parsed?.columnKeys?.length) {
    return {
      columnKeys: parsed.columnKeys,
      columnWidths: parsed.columnWidths ?? {},
      sortConfig: parsed.sortConfig ?? null,
    }
  }
  return { columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null }
}

function saveColumnLayout(layout: ColumnLayout) {
  saveUiSettingIfChanged(COLUMN_LAYOUT_KEY, layout)
}

// ─── Column definitions builder ───────────────────────────────────────────────

function buildAvailableColumns(
  t: TFunction,
  // AP-16: Der Mandant entscheidet, ob hier „Projekt" oder „Auftrag" steht.
  terms: Terms,
  timeConfig: TenantTimeConfig | null,
  isManager: boolean,
): ColumnFieldDef[] {
  const cols: ColumnFieldDef[] = []

  // ── Verdacht (Phase 9) ──────────────────────────────────────────────────────
  if (isManager) {
    cols.push({ key: 'suspicion', label: t('timeTracking.suspicion', { defaultValue: 'Verdacht' }), category: 'core' })
  }

  // ── Kerndaten (Mitarbeiter) ──────────────────────────────────────────────────
  if (isManager) {
    cols.push({ key: 'employee',  label: t('timeTracking.employee',  { defaultValue: 'Mitarbeiter' }), category: 'core' })
    cols.push({ key: 'vorname',   label: t('timeTracking.vorname',   { defaultValue: 'Vorname' }),      category: 'core' })
    cols.push({ key: 'nachname',  label: t('timeTracking.nachname',  { defaultValue: 'Nachname' }),     category: 'core' })
  }
  cols.push({ key: 'qualifikation', label: t('timeTracking.qualifikation', { defaultValue: 'Qualifikation' }), category: 'core' })
  cols.push({ key: 'mode',        label: t('timeTracking.mode',        { defaultValue: 'Modus' }),       category: 'core' })
  cols.push({ key: 'note',        label: t('timeTracking.note',        { defaultValue: 'Notiz' }),       category: 'core' })
  cols.push({ key: 'managerNote', label: t('timeTracking.managerNote', { defaultValue: 'Begründung' }),  category: 'core' })
  cols.push({ key: 'status',      label: t('timeTracking.status',      { defaultValue: 'Status' }),      category: 'core' })

  // ── Projektdaten ─────────────────────────────────────────────────────────────
  cols.push({ key: 'project',               label: terms.project,                                                                category: 'project' })
  cols.push({ key: 'projektnummer',         label: terms.number,                                                                 category: 'project' })
  cols.push({ key: 'activityType',          label: t('timeTracking.activity',              { defaultValue: 'Tätigkeit' }),           category: 'project' })
  cols.push({ key: 'projectposition',       label: terms.position,                                                               category: 'project' })
  cols.push({ key: 'nummerprojektposition', label: terms.positionNumber,                                                         category: 'project' })
  cols.push({ key: 'arbeitsschritt',        label: t('timeTracking.arbeitsschritt',        { defaultValue: 'Arbeitsschritt' }),      category: 'project' })
  cols.push({ key: 'idarbeitsschritt',      label: t('timeTracking.idarbeitsschritt',      { defaultValue: 'ID Arbeitsschritt' }),   category: 'project' })
  cols.push({ key: 'betriebsstaette',       label: t('timeTracking.betriebsstaette',       { defaultValue: 'Betriebsstätte' }),      category: 'project' })

  // ── Zeitdaten ────────────────────────────────────────────────────────────────
  cols.push({ key: 'start',    label: t('timeTracking.start',    { defaultValue: 'Start' }),       category: 'time' })
  cols.push({ key: 'end',      label: t('timeTracking.end',      { defaultValue: 'Ende' }),         category: 'time' })
  cols.push({ key: 'duration', label: t('timeTracking.duration', { defaultValue: 'Dauer' }),        category: 'time', summable: true, align: 'right' })
  cols.push({ key: 'break',    label: t('timeTracking.break',    { defaultValue: 'Pause' }),        category: 'time', summable: true, align: 'right' })
  cols.push({ key: 'gpsStart', label: t('timeTracking.gpsStart', { defaultValue: 'GPS Start' }),    category: 'time' })
  cols.push({ key: 'gpsEnd',   label: t('timeTracking.gpsEnd',   { defaultValue: 'GPS Ende' }),     category: 'time' })

  // ── Custom fields – Zeiterfassung (Typ A, pro Buchung) ───────────────────────
  if (timeConfig?.timeEntryCustomFieldsSchema) {
    for (const field of timeConfig.timeEntryCustomFieldsSchema) {
      cols.push({ key: `cf_${field.name}`, label: field.label, category: 'customField' })
    }
  }

  // ── Preisfelder ────────────────────────────────────────────────────────────
  //
  // AP-30-F: Sie stehen **immer** in der Liste und tragen ihr Feldrecht. Das
  // `if (priceAllowed)`, das hier stand, war die zweite Wahrheit über dieselbe
  // Spalte: Gebaut wurde sie nach einer Einstellung, geprüft nach einem Flag,
  // und wer die beiden auseinanderlaufen ließ, bekam eine Spalte, die es gibt
  // und die niemand sieht — oder umgekehrt. Gestrichen wird jetzt an **einer**
  // Stelle (`ohneFehlendeRechte`), und zwar aus Anzeige, Auswahl, Sortierung,
  // Filter und Summe zugleich.
  cols.push({
    key: 'selbstkostenstundensatz',
    label: t('timeTracking.selbstkostenstundensatz', { defaultValue: 'SK-Stundensatz' }),
    category: 'price', requiresPermission: 'time.price.view', align: 'right',
  })
  cols.push({
    key: 'verkaufsstundensatz',
    label: t('timeTracking.verkaufsstundensatz', { defaultValue: 'VK-Stundensatz' }),
    category: 'price', requiresPermission: 'time.price.view', align: 'right',
  })
  cols.push({
    key: 'gpSelbstkosten',
    label: t('timeTracking.gpSelbstkosten', { defaultValue: 'GP Selbstkosten' }),
    category: 'price', requiresPermission: 'time.price.view', summable: true, align: 'right',
  })
  cols.push({
    key: 'gpVerkauf',
    label: t('timeTracking.gpVerkauf', { defaultValue: 'GP Verkauf' }),
    category: 'price', requiresPermission: 'time.price.view', summable: true, align: 'right',
  })

  return cols
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function buildAvailableFilterFields(
  t: TFunction,
  terms: Terms,
  projects: { id: string; name: string; projectNumber?: string }[],
  activityTypes: { id: string; name: string }[],
  timeConfig: TenantTimeConfig | null,
  isManager: boolean,
  users: User[],
  positions: { id: string; nummer: number | null; name: string }[],
  steps: { id: string; nummer: number | null; name: string }[],
): FilterFieldDef[] {
  const fields: FilterFieldDef[] = []

  // AP-09-E: Filter-Keys richten sich nach dem Datenbezug-Modus. Im Ref-Layer
  // (INTERNAL/EXTERNAL_API) referenzieren die Buchungen projectRefId/activityRefId;
  // nur im Freitext-Modus (MANUAL) wird auf die Legacy-Felder projectId/activityTypeId
  // gefiltert. Wird der falsche Key benutzt, matcht der Filter im Ref-Modus nichts.
  const projectMode: DataSourceMode = timeConfig?.projectSource ?? 'INTERNAL'
  const activityMode: DataSourceMode = timeConfig?.activityTypeSource ?? 'INTERNAL'
  const projectFilterKey = usesRefLayer(projectMode) ? 'projectRefId' : 'projectId'
  const activityFilterKey = usesRefLayer(activityMode) ? 'activityRefId' : 'activityTypeId'

  // Verdachtsfilter (Phase 9) – nur für Manager sichtbar
  if (isManager) {
    fields.push({ key: 'hasSuspicion', label: t('timeTracking.hasSuspicion', { defaultValue: 'Nur Verdachtsfälle' }), type: 'boolean', category: 'core', operators: ['is'] })
  }

  fields.push({ key: 'dateFrom', label: t('timeTracking.from', { defaultValue: 'Von' }), type: 'datetime', category: 'core', operators: ['is'], defaultTime: '00:00' })
  fields.push({ key: 'dateTo',   label: t('timeTracking.to',   { defaultValue: 'Bis' }), type: 'datetime', category: 'core', operators: ['is'], defaultTime: '23:59' })
  fields.push({
    key: 'status',
    label: t('timeTracking.status', { defaultValue: 'Status' }),
    type: 'select-multi',
    category: 'core',
    options: [
      { value: 'DRAFT',    label: t('timeTracking.draft',    { defaultValue: 'Entwurf' }) },
      { value: 'SAVED',    label: t('timeTracking.saved',    { defaultValue: 'Gespeichert' }) },
      { value: 'SYNCED',   label: t('timeTracking.synced',   { defaultValue: 'Synchronisiert' }) },
      { value: 'REVIEWED', label: t('timeTracking.reviewed', { defaultValue: 'Geprüft' }) },
      { value: 'APPROVED', label: t('timeTracking.approved', { defaultValue: 'Genehmigt' }) },
    ],
    operators: ['is', 'is_not'],
  })
  if (isManager) {
    fields.push({
      key: 'userId',
      label: t('timeTracking.employee', { defaultValue: 'Mitarbeiter' }),
      type: 'select-multi',
      category: 'core',
      options: users.map((u) => ({
        value: u.id,
        label: u.profile?.firstName
          ? `${u.profile.firstName}${u.profile.lastName ? ' ' + u.profile.lastName : ''}`
          : u.email ?? '—',
      })),
      operators: ['is', 'is_not'],
    })
  }
  // „Läuft gerade": findet offene Stempelbuchungen — in der Auswertung ist das
  // die Frage nach vergessenen Ausstempelungen, korrigiert wird gleich hier.
  // Wer wissen will, wer jetzt woran arbeitet, nimmt „Aktuelle Tätigkeiten".
  if (isManager) {
    fields.push({ key: 'isRunning', label: t('timeTracking.isRunning', { defaultValue: 'Läuft gerade' }), type: 'boolean', category: 'core', operators: ['is'] })
  }
  fields.push({ key: 'note', label: t('timeTracking.note', { defaultValue: 'Notiz' }), type: 'text', category: 'core', operators: ['contains', 'not_contains', 'is', 'is_not'] })
  fields.push({ key: 'qualifikation', label: t('timeTracking.qualifikation', { defaultValue: 'Qualifikation' }), type: 'text', category: 'core', operators: ['contains', 'not_contains', 'is', 'is_not'] })
  fields.push({ key: projectFilterKey, label: terms.project, type: 'select-multi', category: 'project', options: projects.map((p) => ({ value: p.id, label: p.projectNumber ? `${p.projectNumber} · ${p.name}` : p.name })), operators: ['is', 'is_not'] })
  fields.push({ key: activityFilterKey, label: t('timeTracking.activity', { defaultValue: 'Tätigkeit' }), type: 'select-multi', category: 'project', options: activityTypes.map((a) => ({ value: a.id, label: a.name })), operators: ['is', 'is_not'] })
  fields.push({ key: 'sf_projektnummer',   label: terms.number,   type: 'text', category: 'project', operators: ['contains', 'not_contains', 'is', 'is_not'] })
  // Projektposition + Arbeitsschritt: im Ref-Modus durchsuchbare Dropdowns aus dem
  // Ref-Layer (Filter auf positionRefId/stepRefId), im Freitext-Modus Textsuche
  // (quellenübergreifend via positionFilter/stepFilter im Backend).
  if (usesRefLayer(projectMode)) {
    fields.push({ key: 'positionRefId', label: terms.position, type: 'select-multi', category: 'project', options: positions.map((p) => ({ value: p.id, label: p.nummer != null ? `${p.nummer} · ${p.name}` : p.name })), operators: ['is', 'is_not'] })
    fields.push({ key: 'stepRefId',     label: t('timeTracking.arbeitsschritt',  { defaultValue: 'Arbeitsschritt' }),  type: 'select-multi', category: 'project', options: steps.map((s) => ({ value: s.id, label: s.nummer != null ? `${s.nummer} · ${s.name}` : s.name })), operators: ['is', 'is_not'] })
  } else {
    fields.push({ key: 'sf_projektposition', label: terms.position, type: 'text', category: 'project', operators: ['contains', 'not_contains', 'is', 'is_not'] })
    fields.push({ key: 'sf_arbeitsschritt',  label: t('timeTracking.arbeitsschritt',  { defaultValue: 'Arbeitsschritt' }),  type: 'text', category: 'project', operators: ['contains', 'not_contains', 'is', 'is_not'] })
  }
  fields.push({ key: 'sf_betriebsstaette', label: t('timeTracking.betriebsstaette', { defaultValue: 'Betriebsstätte' }), type: 'text', category: 'project', operators: ['is', 'is_not', 'contains', 'not_contains'] })

  if (timeConfig?.timeEntryCustomFieldsSchema) {
    for (const field of timeConfig.timeEntryCustomFieldsSchema) {
      let inputType: FilterFieldDef['type']
      let operators: FilterFieldDef['operators']
      if (field.type === 'select') {
        inputType = 'select'; operators = ['is', 'is_not', 'contains', 'not_contains']
      } else if (field.type === 'checkbox') {
        inputType = 'boolean'; operators = ['is', 'is_not']
      } else if (field.type === 'number') {
        inputType = 'number'; operators = ['is', 'is_not', 'contains', 'not_contains', 'gt', 'lt']
      } else {
        inputType = 'text'; operators = ['is', 'is_not', 'contains', 'not_contains']
      }
      fields.push({ key: `cf_${field.name}`, label: field.label, type: inputType, category: 'customField', options: field.options?.map((s) => ({ value: s, label: s })), operators })
    }
  }

  // Typ B – Lohnverrechnung-Zusatzfelder (lv_*): über DailyRecord-Zweischritt-Query filterbar
  if (timeConfig?.customFieldsSchema) {
    for (const field of timeConfig.customFieldsSchema) {
      let inputType: FilterFieldDef['type']
      let operators: FilterFieldDef['operators']
      if (field.type === 'select') {
        inputType = 'select'; operators = ['is', 'is_not', 'contains', 'not_contains']
      } else if (field.type === 'checkbox') {
        inputType = 'boolean'; operators = ['is', 'is_not']
      } else if (field.type === 'number') {
        inputType = 'number'; operators = ['is', 'is_not', 'contains', 'not_contains', 'gt', 'lt']
      } else {
        inputType = 'text'; operators = ['is', 'is_not', 'contains', 'not_contains']
      }
      fields.push({ key: `lv_${field.name}`, label: field.label, type: inputType, category: 'lohnverrechnung', options: field.options?.map((s) => ({ value: s, label: s })), operators })
    }
  }

  return fields
}

function filtersToApiParams(
  filterValues: Record<string, ActiveFilter>,
) {
  const params: Record<string, unknown> = {}
  const customFieldFilters: Record<string, { op: string; value: string }> = {}
  const standardFieldFilters: Record<string, { op: string; value: string }> = {}
  const lvFieldFilters: Record<string, { op: string; value: string }> = {}

  for (const [key, active] of Object.entries(filterValues)) {
    if (!active.value) continue
    // KA-18: Der Zeitanteil zählt. Ohne Uhrzeit (oder bei der jeweiligen
    // Tagesgrenze 00:00 / 23:59) geht der Filter weiterhin auf das
    // timezone-neutrale `date`-Feld — nur ein reines Datum vermeidet den früheren
    // UTC-Offset-Fehler ("2026-04-09T00:00" ohne Zeitzone liest Node.js als
    // Lokalzeit, was auf UTC-Servern den Vortag mit hineinzieht).
    //
    // Steht eine echte Uhrzeit darin, filtert das Backend auf `startTime`. Der
    // Zeitstempel bekommt dabei ein ausdrückliches `Z`: Buchungszeiten sind
    // Wall-Clock-als-UTC gespeichert (siehe .claude/rules/zeit-und-datum.md),
    // die abgelesene Uhrzeit ist also genau der richtige Vergleichswert.
    if (key === 'dateFrom' || key === 'dateTo') {
      const isFrom = key === 'dateFrom'
      const time = active.value.length > 10 ? active.value.slice(11, 16) : ''
      if (time && time !== (isFrom ? '00:00' : '23:59')) {
        // „Von 08:00" meint den Beginn, „bis 09:00" das Ende: Sonst bliebe eine
        // Buchung von 08:32 bis 10:00 in der Liste, obwohl sie weit über die
        // Grenze hinausläuft (gemeldet am 6.9.2026).
        params[isFrom ? 'startTimeFrom' : 'endTimeTo'] = `${active.value.slice(0, 10)}T${time}:00.000Z`
      } else {
        params[isFrom ? 'dateFrom' : 'dateTo'] = active.value.slice(0, 10)
      }
      continue
    }
    if (key === 'status') {
      const statuses = active.value ? active.value.split(',').filter(Boolean) : []
      if (statuses.length === 0) continue
      // KA-16: „ist nicht" schloss früher nur statuses[0] aus.
      if (active.operator === 'is_not') {
        if (statuses.length === 1) params['statusNot'] = statuses[0]
        else params['statusNotIn'] = statuses.join(',')
      }
      else if (statuses.length === 1) params['status'] = statuses[0]
      else params['statusIn'] = statuses.join(',')
      continue
    }
    if (key === 'note') {
      if (active.operator === 'contains') params['noteContains'] = active.value
      else if (active.operator === 'not_contains') params['noteNotContains'] = active.value
      else if (active.operator === 'is') params['noteContains'] = active.value
      else if (active.operator === 'is_not') params['noteNotContains'] = active.value
      continue
    }
    if (key === 'qualifikation') {
      if (active.operator === 'contains' || active.operator === 'is') params['qualifikationContains'] = active.value
      else if (active.operator === 'not_contains' || active.operator === 'is_not') params['qualifikationIsNot'] = active.value
      continue
    }
    if (key === 'hasSuspicion') { params['hasSuspicion'] = active.value === 'true' ? true : active.value === 'false' ? false : undefined; continue }
    if (key === 'isRunning') { params['isRunning'] = active.value === 'true' ? true : active.value === 'false' ? false : undefined; continue }
    if (key === 'userId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['userIdNot'] = ids[0]
        else params['userIdsNot'] = ids.join(',')
      } else {
        // is: ein oder mehrere User → bei einem: userId, sonst: userIds (kommasep.)
        if (ids.length === 1) params['userId'] = ids[0]
        else params['userIds'] = ids.join(',')
      }
      continue
    }
    if (key === 'projectId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['projectIdNot'] = ids[0]
        else params['projectIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['projectId'] = ids[0]
      else params['projectIds'] = ids.join(',')
      continue
    }
    // AP-09-E: Ref-Layer-Projektfilter (INTERNAL/EXTERNAL_API)
    if (key === 'projectRefId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['projectRefIdNot'] = ids[0]
        else params['projectRefIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['projectRefId'] = ids[0]
      else params['projectRefIds'] = ids.join(',')
      continue
    }
    if (key === 'activityTypeId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['activityTypeIdNot'] = ids[0]
        else params['activityTypeIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['activityTypeId'] = ids[0]
      else params['activityTypeIds'] = ids.join(',')
      continue
    }
    // AP-09-E: Ref-Layer-Tätigkeitsfilter (INTERNAL/EXTERNAL_API)
    if (key === 'activityRefId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['activityRefIdNot'] = ids[0]
        else params['activityRefIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['activityRefId'] = ids[0]
      else params['activityRefIds'] = ids.join(',')
      continue
    }
    // Projektposition-/Arbeitsschritt-Dropdown (Ref-Modus): Filter auf RefId.
    // „ist nicht" ist backend-seitig NULL-inklusiv (leere Einträge erscheinen).
    if (key === 'positionRefId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['positionRefIdNot'] = ids[0]
        else params['positionRefIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['positionRefId'] = ids[0]
      else params['positionRefIds'] = ids.join(',')
      continue
    }
    if (key === 'stepRefId') {
      const ids = active.value ? active.value.split(',').filter(Boolean) : []
      if (ids.length === 0) continue
      // KA-16: bei „ist nicht" ALLE gewählten ausschließen, nicht nur ids[0]
      if (active.operator === 'is_not') {
        if (ids.length === 1) params['stepRefIdNot'] = ids[0]
        else params['stepRefIdsNot'] = ids.join(',')
      }
      else if (ids.length === 1) params['stepRefId'] = ids[0]
      else params['stepRefIds'] = ids.join(',')
      continue
    }
    // Projektnummer/-position/Arbeitsschritt: quellenübergreifend (Ref-Layer ↔
    // standardFields) — ein reiner standardFields-Filter fände im Ref-Modus nichts.
    if (key === 'sf_projektnummer')   { params['projektNummerFilter'] = JSON.stringify({ op: active.operator, value: active.value }); continue }
    if (key === 'sf_projektposition') { params['positionFilter']      = JSON.stringify({ op: active.operator, value: active.value }); continue }
    if (key === 'sf_arbeitsschritt')  { params['stepFilter']          = JSON.stringify({ op: active.operator, value: active.value }); continue }
    if (key.startsWith('sf_')) { standardFieldFilters[key.slice(3)] = { op: active.operator, value: active.value }; continue }
    if (key.startsWith('cf_')) { customFieldFilters[key.slice(3)] = { op: active.operator, value: active.value }; continue }
    if (key.startsWith('lv_')) { lvFieldFilters[key.slice(3)] = { op: active.operator, value: active.value }; continue }
  }

  if (Object.keys(customFieldFilters).length > 0) params['customFieldFilters'] = JSON.stringify(customFieldFilters)
  if (Object.keys(standardFieldFilters).length > 0) params['standardFieldFilters'] = JSON.stringify(standardFieldFilters)
  if (Object.keys(lvFieldFilters).length > 0) params['lvFieldFilters'] = JSON.stringify(lvFieldFilters)
  return params
}

// ─── Internal types ────────────────────────────────────────────────────────────

// KA-34: Prüfen kennt nur „geprüft“ und eine Notiz. Die frühere Auswahl „Zurückweisen“
// schickte `status`, das der Server nicht kennt — jeder Klick endete mit 400.
interface ReviewingEntry { id: string; managerNote: string }

// ─── Category labels for column dropdown ─────────────────────────────────────

const COLUMN_CATEGORY_LABELS: Record<string, string> = {
  core: 'Kerndaten',
  project: 'Projektdaten',
  time: 'Zeitdaten',
  customField: 'Zusatzfelder Zeiterfassung',
  price: 'Preisfelder',
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SummaryPage() {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const hoursFormat = useHoursFormat()
  // AP-30-D: Prüfen und Freigeben hängt am Prüfrecht …
  const isManager = erfuelltRecht('time.entry.review', user?.permissions)
  // READONLY ("Nur lesen"): darf die komplette Einträge-Liste einsehen (alle Spalten,
  // alle Mitarbeiter), aber keine Schreib-Aktionen (Bearbeiten/Prüfen/Genehmigen/Löschen)
  // und keinen Export ausführen. Schreib-Buttons hängen weiterhin an `isManager`
  // (für READONLY false); die Lese-Sicht nutzt `canViewAll`.
  // … das Sehen fremder Buchungen am Geltungsbereich. Genau hier versagte
  // die Leiter: „Nur Lesen" sieht mehr als ein Mitarbeiter und darf
  // weniger als er — zwei Achsen, die eine Rangzahl nicht trennen kann.
  const canViewAll = erfuelltRecht('time.entry.read.all', user?.permissions)
  // KA-34: Der Export hängt am Exportrecht — dasselbe, das der Server verlangt.
  // Bis 28.9.2026 hing er am Prüfrecht; eine eigene Rolle mit nur einem der
  // beiden Rechte sah den Knopf und bekam 403 oder sah ihn nie.
  const canExport = erfuelltRecht('time.entry.export', user?.permissions)

  // Filter state
  // AP-40 6.4: der Zeitraum aus der Adresse (Kachel des Dashboards) — einmal beim Öffnen gelesen.
  const [suchParameter] = useSearchParams()
  const [adresse] = useState(() =>
    filterAusAdresse(suchParameter, [
      { key: 'dateFrom', label: '', type: 'date', category: 'core', operators: ['is'] },
      { key: 'dateTo', label: '', type: 'date', category: 'core', operators: ['is'] },
    ]),
  )
  const [filterValues, setFilterValues] = useState<Record<string, ActiveFilter>>({})

  // Pagination
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(() => {
    const v = loadUiSetting<number>(PAGE_SIZE_KEY, 50)
    return Number.isFinite(v) && v > 0 ? v : 50
  })
  const [pageSizeInput, setPageSizeInput] = useState<string>(() => String(limit))
  const pageSizeTimer = useRef<NodeJS.Timeout | null>(null)
  const [addColPos, setAddColPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

  // Data
  const [entries, setEntries] = useState<TimeEntry[]>([])
  const [serverTotals, setServerTotals] = useState<Record<string, number>>({})
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit, totalPages: 0 })

  // Reference data
  const [projects, setProjects] = useState<{ id: string; name: string; projectNumber?: string; externalId?: string | null }[]>([])
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([])
  // Standardfeld-Definitionen + Betriebsstätten — für den Edit-Dialog (wie Buchungsmaske)
  const [standardFieldDefs, setStandardFieldDefs] = useState<StandardFieldDefinition[]>([])
  const [betriebsstaetteItems, setBetriebsstaetteItems] = useState<StandardSelectionItem[]>([])
  // Kaskade Edit (Legacy-Modus): Projektpositionen / Arbeitsschritte
  const [editProjektpositionen, setEditProjektpositionen] = useState<StandardSelectionItem[]>([])
  const [editArbeitsschritte, setEditArbeitsschritte] = useState<StandardSelectionItem[]>([])
  // AP-09-E: Filter-Optionen folgen dem Datenbezug-Modus (Ref-Layer vs. Legacy).
  // Bewusst getrennt von `projects`/`activityTypes`, die der Inline-Editor (Legacy) nutzt.
  const [filterProjects, setFilterProjects] = useState<{ id: string; name: string; projectNumber?: string }[]>([])
  const [filterActivities, setFilterActivities] = useState<{ id: string; name: string }[]>([])
  // Ref-Layer-Optionen für die Projektposition-/Arbeitsschritt-Dropdowns (nur Ref-Modus)
  const [filterPositions, setFilterPositions] = useState<{ id: string; nummer: number | null; name: string }[]>([])
  const [filterSteps, setFilterSteps] = useState<{ id: string; nummer: number | null; name: string }[]>([])
  const [timeConfig, setTimeConfig] = useState<TenantTimeConfig | null>(null)
  const [users, setUsers] = useState<User[]>([])
  const terms = useTerminology()
  // AP-30-F: **Eine** Stelle streicht, was kein Recht hat — aus Anzeige,
  // Auswahl, Sortierung, Filter und Summe zugleich.
  const availableColumns = ohneFehlendeRechte(
    buildAvailableColumns(t, terms, timeConfig, canViewAll),
    (p) => erfuelltRecht(p, user?.permissions),
  )
  const [columnLayout, setColumnLayout] = useState<ColumnLayout>(() => loadColumnLayout())
  const [columnConfigMode, setColumnConfigMode] = useState(false)
  const [addColDropdownOpen, setAddColDropdownOpen] = useState(false)
  const addColRef = useRef<HTMLDivElement>(null)

  // Derived layout state
  const columnWidths = columnLayout.columnWidths ?? {}
  const sortConfig = columnLayout.sortConfig ?? null

  // DnD state
  const draggedKeyRef = useRef<string | null>(null)
  const [dragOverKey, setDragOverKey] = useState<string | null>(null)

  // Column resize state
  const resizeRef = useRef<{ key: string; startX: number; startWidth: number } | null>(null)

  // Persist column layout
  // AP-23: `…IfChanged` — dieser Effekt läuft auch beim Mount, und das bloße
  // Öffnen der Liste darf keine eigene Einstellung anlegen (sonst griffe der
  // Mandanten-Standard für diesen Benutzer nie wieder).
  useEffect(() => { saveColumnLayout(columnLayout) }, [columnLayout])

  // Close add-column dropdown on outside click
  useEffect(() => {
    if (!addColDropdownOpen) return
    const handler = (e: MouseEvent) => {
      if (addColRef.current && !addColRef.current.contains(e.target as Node)) setAddColDropdownOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [addColDropdownOpen])

  // Column resize mouse handlers (document-level)
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!resizeRef.current) return
      const delta = e.clientX - resizeRef.current.startX
      const newWidth = Math.max(60, resizeRef.current.startWidth + delta)
      setColumnLayout((prev) => ({
        ...prev,
        columnWidths: { ...(prev.columnWidths ?? {}), [resizeRef.current!.key]: newWidth },
      }))
    }
    const onUp = () => { resizeRef.current = null; document.body.style.cursor = '' }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reviewingEntryId, setReviewingEntryId] = useState<string | null>(null)
  const [reviewData, setReviewData] = useState<ReviewingEntry>({ id: '', managerNote: '' })
  const [reviewLoading, setReviewLoading] = useState(false)

  // Phase 9: Suspicion clear
  const [clearingEntryId, setClearingEntryId] = useState<string | null>(null)

  // Edit-Dialog
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ date: '', startTime: '', endTime: '', breakMinutes: '', note: '', projectId: '', activityTypeId: '', projectRefId: '', activityRefId: '', selectedProjektposition: '', selectedArbeitsschritt: '' })
  // Editierbare JSON-Felder (dynamisch aus dem Eintrag) + read-only Snapshot-Infos
  const [editStandardFields, setEditStandardFields] = useState<Record<string, string>>({})
  const [editCustomFields, setEditCustomFields] = useState<Record<string, string>>({})
  const [editReadonly, setEditReadonly] = useState<{ label: string; value: string }[]>([])
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // Genehmigen / Löschen (zweistufiger Workflow + Löschfreigabe)
  const [actionEntryId, setActionEntryId] = useState<string | null>(null)

  const debounceTimer = useRef<NodeJS.Timeout | null>(null)

  // ── Column helpers ────────────────────────────────────────────────────────────
  const shownColumns = columnLayout.columnKeys
    .map((k) => availableColumns.find((c) => c.key === k))
    .filter(Boolean) as ColumnFieldDef[]

  const availableToAddColumns = availableColumns.filter((c) => !columnLayout.columnKeys.includes(c.key))

  const groupedAvailableColumns = availableToAddColumns.reduce<Record<string, ColumnFieldDef[]>>((acc, c) => {
    if (!acc[c.category]) acc[c.category] = []
    acc[c.category].push(c)
    return acc
  }, {})

  const handleAddColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: [...prev.columnKeys, key] }))
    setAddColDropdownOpen(false)
  }

  const handleRemoveColumn = (key: string) => {
    setColumnLayout((prev) => ({ ...prev, columnKeys: prev.columnKeys.filter((k) => k !== key) }))
  }

  // ── Drag & Drop handlers ───────────────────────────────────────────────────────
  const handleDragStart = (key: string) => { draggedKeyRef.current = key }
  const handleDragOver = (e: React.DragEvent, key: string) => {
    e.preventDefault()
    if (draggedKeyRef.current && draggedKeyRef.current !== key) setDragOverKey(key)
  }
  const handleDrop = (targetKey: string) => {
    const srcKey = draggedKeyRef.current
    if (!srcKey || srcKey === targetKey) { setDragOverKey(null); return }
    setColumnLayout((prev) => {
      const keys = [...prev.columnKeys]
      const from = keys.indexOf(srcKey)
      const to = keys.indexOf(targetKey)
      if (from < 0 || to < 0) return prev
      keys.splice(from, 1)
      keys.splice(to, 0, srcKey)
      return { ...prev, columnKeys: keys }
    })
    draggedKeyRef.current = null
    setDragOverKey(null)
  }
  const handleDragEnd = () => { draggedKeyRef.current = null; setDragOverKey(null) }

  // ── Sort handler ───────────────────────────────────────────────────────────────
  // Columns with server-side sort support (direct Prisma fields or one-level relations)
  const SERVER_SORTABLE = SERVER_SORTABLE_KEYS

  const handleSort = (key: string) => {
    if (!SERVER_SORTABLE.has(key)) return // non-sortable columns (JSON, custom fields)
    setColumnLayout((prev) => {
      const current = prev.sortConfig
      let next: ColumnSortConfig | null
      if (!current || current.key !== key) next = { key, dir: 'asc' }
      else if (current.dir === 'asc') next = { key, dir: 'desc' }
      else next = null // third click → remove sort
      return { ...prev, sortConfig: next }
    })
    setPage(1)
  }

  // ── Resize handler ─────────────────────────────────────────────────────────────
  const handleResizeStart = (e: React.MouseEvent, key: string) => {
    e.preventDefault()
    e.stopPropagation()
    const th = (e.target as HTMLElement).closest('th')
    const startWidth = th ? th.getBoundingClientRect().width : (columnWidths[key] ?? 120)
    resizeRef.current = { key, startX: e.clientX, startWidth }
    document.body.style.cursor = 'col-resize'
  }

  // ── Fetch entries ─────────────────────────────────────────────────────────────
  const fetchEntries = useCallback(
    async (currentFilterValues: Record<string, ActiveFilter>, currentPage: number, currentSort?: ColumnSortConfig | null, currentLimit: number = limit) => {
      setLoading(true)
      setError(null)
      try {
        const apiParams = filtersToApiParams(currentFilterValues)
        const sortParams = currentSort ? { sortBy: currentSort.key, sortDir: currentSort.dir } : {}
        const { data } = await timeEntriesApi.list({ ...apiParams, ...sortParams, page: currentPage, limit: currentLimit } as Parameters<typeof timeEntriesApi.list>[0])
        setEntries(data.data)
        setPagination({ total: data.meta.total, page: data.meta.page, limit: data.meta.limit, totalPages: data.meta.totalPages })
        setServerTotals(((data.meta as { totals?: Record<string, number> }).totals) ?? {})
      } catch (err) {
        console.error('Failed to fetch time entries', err)
        setError(t('timeTracking.errorFetchingEntries', { defaultValue: 'Zeiteinträge konnten nicht geladen werden.' }))
      } finally {
        setLoading(false)
      }
    },
    [t, limit]
  )

  const handleFiltersChange = useCallback(
    (newValues: Record<string, ActiveFilter>) => {
      setFilterValues(newValues)
      setPage(1)
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => fetchEntries(newValues, 1), 500)
    },
    [fetchEntries]
  )

  const handlePageChange = useCallback(
    (newPage: number) => { setPage(newPage); fetchEntries(filterValues, newPage, sortConfig) },
    [filterValues, fetchEntries, sortConfig]
  )

  const handlePageSizeChange = useCallback(
    (newLimit: number) => {
      if (!Number.isFinite(newLimit) || newLimit < 1) return
      saveUiSetting(PAGE_SIZE_KEY, newLimit)
      setLimit(newLimit)
      setPageSizeInput(String(newLimit))
      setPage(1)
      fetchEntries(filterValues, 1, sortConfig, newLimit)
    },
    [filterValues, fetchEntries, sortConfig]
  )

  const handlePageSizeInput = useCallback(
    (val: string) => {
      setPageSizeInput(val)
      if (pageSizeTimer.current) clearTimeout(pageSizeTimer.current)
      pageSizeTimer.current = setTimeout(() => {
        const n = parseInt(val, 10)
        if (Number.isFinite(n) && n >= 1 && n !== limit) handlePageSizeChange(n)
      }, 500)
    },
    [handlePageSizeChange, limit]
  )

  // ── Load reference data ───────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const [projectsRes, activitiesRes, configRes] = await Promise.all([
          timeConfigApi.getProjects(),
          timeConfigApi.getActivityTypes(),
          // clock-settings statt des ADMIN-only GET /time-config: sonst wirft dieser
          // Call für MANAGER eine 403, die das gesamte Promise.all abbricht und alle
          // Dropdown-/Referenzdaten (Projekte, Tätigkeiten, Standardfelder, Betriebsstätten)
          // leer lässt. clock-settings liefert dieselben von der Liste benötigten Felder.
          timeConfigApi.getClockSettings(),
        ])
        setProjects(projectsRes.data)
        setActivityTypes(activitiesRes.data)
        setTimeConfig(configRes.data)

        // Standardfeld-Definitionen + Betriebsstätten für den Edit-Dialog laden
        // (gleiche Quellen wie die Buchungsmaske in ClockPage).
        timeConfigApi.getStandardFields()
          .then(({ data }) => setStandardFieldDefs(Array.isArray(data) ? data : []))
          .catch(() => { /* Defaults bleiben leer */ })
        timeConfigApi.getStandardSelectionItems('betriebsstaette')
          .then(({ data }) => setBetriebsstaetteItems(Array.isArray(data) ? data.filter((i) => i.isActive) : []))
          .catch(() => { /* Defaults bleiben leer */ })

        // AP-09-E: Auswertungs-Filter müssen denselben Datenbezug wie die
        // Buchungsmaske respektieren. Im Ref-Modus (INTERNAL/EXTERNAL_API) liegen
        // Projekte/Tätigkeiten im Ref-Layer; der Legacy-Endpoint /time-config/projects
        // ist dort leer → sonst bleiben die Such-Dropdowns leer.
        const projMode = configRes.data.projectSource ?? 'INTERNAL'
        const actMode = configRes.data.activityTypeSource ?? 'INTERNAL'
        const [fProjects, fActivities] = await Promise.all([
          usesRefLayer(projMode)
            ? picklistsApi.projects().then((r) => r.data.data.map((p) => ({ id: p.id, name: p.name, projectNumber: p.projectNumber })))
            : Promise.resolve(projectsRes.data.map((p) => ({ id: p.id, name: p.name, projectNumber: p.projectNumber }))),
          usesRefLayer(actMode)
            ? picklistsApi.activities().then((r) => r.data.data.map((a) => ({ id: a.id, name: a.name })))
            : Promise.resolve(activitiesRes.data.map((a) => ({ id: a.id, name: a.name }))),
        ])
        setFilterProjects(fProjects)
        setFilterActivities(fActivities)

        // Ref-Modus: Positionen + Arbeitsschritte für die Filter-Dropdowns laden
        // (gesamter Auswahlbaum des Mandanten via Bundle-Endpoint).
        if (usesRefLayer(projMode)) {
          picklistsApi.bundle()
            .then((r) => {
              setFilterPositions(r.data.data.positions.map((p) => ({ id: p.id, nummer: p.nummer, name: p.name })))
              setFilterSteps(r.data.data.steps.map((s) => ({ id: s.id, nummer: s.nummer, name: s.name })))
            })
            .catch(() => { /* Dropdowns bleiben leer */ })
        }

        // Users separat laden – Fehler hier sollen die restlichen Filterdaten nicht blockieren
        usersApi.list({ limit: 100, kind: 'EMPLOYEE' }).then((res) => setUsers(res.data.data)).catch(() => {
          console.warn('Could not load users for filter dropdown')
        })
      } catch (err) {
        console.error('Failed to load filter data', err)
        setError(t('timeTracking.errorLoadingFilters', { defaultValue: 'Filterdaten konnten nicht geladen werden.' }))
      }
    }
    load()
  }, [t])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEntries(filterValues, page, sortConfig) }, [fetchEntries])

  // Re-fetch when sort changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchEntries(filterValues, page, sortConfig) }, [sortConfig])

  // ── Format helpers ────────────────────────────────────────────────────────────
  // Dauer gemäß tenant-weitem Stunden-Format (Dezimal ohne überflüssige Nullen / H:MM)
  const formatDuration = (minutes: number): string => formatHoursWithUnit(minutes, hoursFormat)
  const formatDate = (dateStr: string): string => fmtDateLong(dateStr)
  const formatTime = (timeStr: string): string => {
    if (!timeStr) return '--:--'
    if (/^\d{2}:\d{2}$/.test(timeStr)) return timeStr
    // Zeiteinträge sind als "Lokalzeit ohne Offset" gespeichert → UTC-Stunden lesen.
    return fmtTimeUTC(timeStr) || '--:--'
  }
  const translateStatus = (status: TimeEntryStatus): string => {
    switch (status) {
      case 'DRAFT': return t('timeTracking.draft', { defaultValue: 'Entwurf' })
      case 'SAVED': return t('timeTracking.saved', { defaultValue: 'Gespeichert' })
      case 'SYNCED': return t('timeTracking.synced', { defaultValue: 'Synchronisiert' })
      case 'REVIEWED': return t('timeTracking.reviewed', { defaultValue: 'Geprüft' })
      case 'APPROVED': return t('timeTracking.approved', { defaultValue: 'Genehmigt' })
      default: return status
    }
  }
  const getStatusColor = (status: TimeEntryStatus): string => {
    switch (status) {
      case 'DRAFT': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case 'SAVED': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'SYNCED': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'REVIEWED': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      case 'APPROVED': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // ── Cell renderer per column key ──────────────────────────────────────────────
  const renderCell = (entry: TimeEntry, col: ColumnFieldDef): React.ReactNode => {
    const dash = <span className="text-muted-foreground">-</span>
    switch (col.key) {
      case 'suspicion': {
        // Verdacht-Anzeige (Phase 9)
        const flags: string[] = Array.isArray(entry.suspicionFlags) ? entry.suspicionFlags : []
        if (entry.suspicionCleared) {
          return (
            <span title={t('timeTracking.suspicionCleared', { defaultValue: 'Als unbedenklich markiert' })} className="inline-flex items-center">
              <ShieldCheck className="h-4 w-4 text-green-500" />
            </span>
          )
        }
        if (flags.length === 0) return dash
        const tooltipText = flags.join('\n')
        return (
          <span title={tooltipText} className="inline-flex items-center gap-1 cursor-default">
            <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">{flags.length}</span>
          </span>
        )
      }
      case 'employee':
        return entry.user
          ? (entry.user.profile?.firstName || entry.user.profile?.lastName
              ? `${entry.user.profile?.firstName ?? ''} ${entry.user.profile?.lastName ?? ''}`.trim()
              : entry.user.email)
          : dash
      case 'vorname':       return entry.user?.profile?.firstName ?? dash
      case 'nachname':      return entry.user?.profile?.lastName  ?? dash
      case 'qualifikation': return entry.qualifikation ?? dash
      // AP-09-E: Anzeigenamen über Resolver – Ref-Eintrag bevorzugen, sonst Legacy.
      case 'project': {
        // Resolver liefert "ProjektNr – Name" wenn ein Projekt vorhanden, sonst "—".
        // Für die Spalte zeigen wir nur den Namen.
        const ref = entry.projectRef?.name
        const leg = entry.project?.name
        return ref ?? leg ?? dash
      }
      case 'projektnummer': {
        const pn = entry.projectRef?.projectNumber
          ?? entry.project?.projectNumber
          ?? (entry.standardFields as Record<string, unknown> | null)?.projektnummer
        return pn != null ? String(pn) : dash
      }
      case 'activityType': {
        const refName = entry.activityRef?.name
        const legName = entry.activityType?.name
        const color = (entry.activityRef as { color?: string | null } | undefined)?.color
          ?? (entry.activityType as { color?: string } | undefined)?.color
          ?? '#6b7280'
        const name = refName ?? legName
        return name ? (
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: color }}
            />
            {name}
          </span>
        ) : dash
      }
      case 'projectposition': {
        // Bevorzugt Ref-Position, sonst Snapshot aus standardFields.
        if (entry.positionRef) return resolveEntryPositionName({ positionRef: entry.positionRef })
        const sf = (entry.standardFields as Record<string, unknown> | null)?.projektposition
        return sf != null ? String(sf) : dash
      }
      case 'nummerprojektposition': {
        const refNum = entry.positionRef?.nummer
        if (refNum != null) return String(refNum)
        return (entry.standardFields as Record<string, unknown> | null)?.nummerprojektposition != null
          ? String((entry.standardFields as Record<string, unknown>).nummerprojektposition)
          : dash
      }
      case 'arbeitsschritt': {
        if (entry.stepRef) return resolveEntryStepName({ stepRef: entry.stepRef })
        const sf = (entry.standardFields as Record<string, unknown> | null)?.arbeitsschritt
        return sf != null ? String(sf) : dash
      }
      case 'idarbeitsschritt':      return (entry.standardFields as Record<string, unknown> | null)?.idarbeitsschritt != null ? String((entry.standardFields as Record<string, unknown>).idarbeitsschritt) : dash
      case 'betriebsstaette':       return (entry.standardFields as Record<string, unknown> | null)?.betriebsstaette != null ? String((entry.standardFields as Record<string, unknown>).betriebsstaette) : dash
      case 'start': return formatTime(entry.startTime)
      case 'end': return entry.endTime ? formatTime(entry.endTime) : dash
      // AP-24: `duration` ist bereits netto — die Pause hier erneut abzuziehen
      // machte jede Dauer um die Pausenzeit zu klein.
      case 'duration': return entry.duration != null ? formatDuration(Math.max(0, entry.duration)) : dash
      case 'break': return entry.breakMinutes ? `${entry.breakMinutes}min` : dash
      case 'mode': return entry.mode === 'CLOCK' ? '⏱' : '✏'
      case 'note':        return entry.note        ? <span className="truncate max-w-[180px] block" title={entry.note}>{entry.note}</span>        : dash
      case 'managerNote': return entry.managerNote ? <span className="truncate max-w-[180px] block" title={entry.managerNote}>{entry.managerNote}</span> : dash
      case 'gpsStart': {
        if (entry.gpsStartLat == null || entry.gpsStartLng == null) return dash
        const url = `https://maps.google.com/?q=${entry.gpsStartLat},${entry.gpsStartLng}`
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs tabular-nums whitespace-nowrap">
            {entry.gpsStartLat.toFixed(5)}, {entry.gpsStartLng.toFixed(5)}
          </a>
        )
      }
      case 'gpsEnd': {
        if (entry.gpsEndLat == null || entry.gpsEndLng == null) return dash
        const url = `https://maps.google.com/?q=${entry.gpsEndLat},${entry.gpsEndLng}`
        return (
          <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline text-xs tabular-nums whitespace-nowrap">
            {entry.gpsEndLat.toFixed(5)}, {entry.gpsEndLng.toFixed(5)}
          </a>
        )
      }
      case 'status':
        return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(entry.status)}`}>{translateStatus(entry.status)}</span>
      // Preisfelder – direkte Spalten auf TimeEntry (Snapshot aus UserTimeSettings)
      case 'selbstkostenstundensatz': {
        const v = entry.selbstkostenstundensatz
        return v != null ? <span className="tabular-nums">{geldText(Number(v))} €</span> : dash
      }
      case 'verkaufsstundensatz': {
        const v = entry.verkaufsstundensatz
        return v != null ? <span className="tabular-nums">{geldText(Number(v))} €</span> : dash
      }
      case 'gpSelbstkosten': {
        if (entry.selbstkostenstundensatz == null || entry.duration == null) return dash
        const hours = Math.max(0, entry.duration) / 60
        const gp = hours * Number(entry.selbstkostenstundensatz)
        return <span className="tabular-nums">{geldText(gp)} €</span>
      }
      case 'gpVerkauf': {
        if (entry.verkaufsstundensatz == null || entry.duration == null) return dash
        const hours = Math.max(0, entry.duration) / 60
        const gp = hours * Number(entry.verkaufsstundensatz)
        return <span className="tabular-nums">{geldText(gp)} €</span>
      }
      default:
        // Custom fields Typ A (cf_* – pro Buchung)
        if (col.key.startsWith('cf_')) {
          const fieldName = col.key.slice(3)
          const val = (entry.customFields as Record<string, unknown> | null)?.[fieldName]
          return val != null ? String(val) : dash
        }
        return dash
    }
  }

  // ── Totals row computation ────────────────────────────────────────────────────
  const totals: Record<string, string> = {}
  const totalsMinutes: Record<string, number> = {}
  for (const col of shownColumns) {
    const v = serverTotals[col.key]
    if (v == null) continue
    if (col.category === 'time') {
      totalsMinutes[col.key] = v
    } else if (col.category === 'price') {
      totals[col.key] = `${geldText(v)} €`
    } else {
      totals[col.key] = String(v)
    }
  }

  // ── Column count for colspan calculations ─────────────────────────────────────
  // Fixed: "date" (1) + "actions" (1) + configurable columns
  const totalColCount = 1 + shownColumns.length + 1

  // ── Export ────────────────────────────────────────────────────────────────────
  const handleExport = (format: 'xlsx' | 'docx' | 'pdf', orientation?: 'portrait' | 'landscape') => {
    // Alle aktiven Filter in API-Params umwandeln (exakt wie beim Laden der Liste)
    const filterParams = filtersToApiParams(filterValues)

    // Exportiert genau die aktuell sichtbaren Spalten (Datum ist immer erste Spalte)
    const exportColumns = [
      { key: 'date', label: t('timeTracking.date', { defaultValue: 'Datum' }) },
      ...shownColumns.map((c) => ({ key: c.key, label: c.label })),
    ]

    // Aktive Sortierung weitergeben
    const sortBy  = sortConfig?.key
    const sortDir = sortConfig?.dir

    // Lesbaren Filtertext für die Infozeile aufbauen
    const filterFields = buildAvailableFilterFields(t, terms, filterProjects, filterActivities, timeConfig, isManager, users, filterPositions, filterSteps)
    const filterParts: string[] = []
    for (const [key, active] of Object.entries(filterValues)) {
      if (!active.value) continue
      const fieldDef = filterFields.find((f) => f.key === key)
      const label = fieldDef?.label ?? key
      const opLabel: Record<string, string> = { is: '=', is_not: '≠', contains: '~', not_contains: '!~', gt: '>', lt: '<' }
      if (key === 'dateFrom' || key === 'dateTo') {
        // KA-18: Uhrzeit mit ausweisen, wenn sie den Filter tatsächlich einengt.
        const isFrom = key === 'dateFrom'
        const time = active.value.length > 10 ? active.value.slice(11, 16) : ''
        const shown = time && time !== (isFrom ? '00:00' : '23:59')
          ? `${active.value.slice(0, 10)} ${time}`
          : active.value.slice(0, 10)
        filterParts.push(`${isFrom ? 'Von' : 'Bis'}: ${shown}`)
      } else if (key === 'userId' || key === 'projectId' || key === 'activityTypeId' || key === 'status') {
        const ids = active.value.split(',').filter(Boolean)
        const names = ids.map((id) => fieldDef?.options?.find((o) => o.value === id)?.label ?? id).join(', ')
        const opLabel2 = active.operator === 'is_not' ? '≠' : '='
        filterParts.push(`${label} ${opLabel2} ${names}`)
      } else {
        filterParts.push(`${label} ${opLabel[active.operator] ?? active.operator} ${active.value}`)
      }
    }
    const filterInfo = filterParts.length > 0 ? filterParts.join(' · ') : undefined

    return timeEntriesApi.exportRequest({
      ...filterParams,
      format,
      columns: exportColumns,
      sortBy,
      sortDir,
      filterInfo,
      orientation,
    } as Parameters<typeof timeEntriesApi.exportRequest>[0])
  }

  // ── Edit ──────────────────────────────────────────────────────────────────────
  /** ISO-DateTime oder "HH:MM" → "HH:MM" für <input type="time"> */
  const toTimeInput = (val: string | null | undefined): string => {
    if (!val) return ''
    if (/^\d{2}:\d{2}$/.test(val)) return val
    // UTC-Stunden lesen (Wall-Clock-als-UTC-Konvention), sonst Offset-Verschiebung.
    return fmtTimeUTC(val)
  }

  /** Übersetzt bekannte englische class-validator-Meldungen ins Deutsche */
  const translateApiError = (msg: string | null | undefined): string => {
    if (!msg) return t('timeTracking.errorSaving', { defaultValue: 'Speichern fehlgeschlagen.' })
    // Bekannte Backend-Keys (z.B. Monats-Sperre) zuerst in Klartext übersetzen.
    const mapped = translateApiErrorKey(msg, '')
    if (mapped && mapped !== msg) return mapped
    const patterns: [RegExp, string][] = [
      [/must be a valid ISO 8601 date string/i, 'muss eine gültige Uhrzeit sein'],
      [/must be longer than or equal to (\d+)/i, 'muss mindestens $1 Zeichen lang sein'],
      [/must be shorter than or equal to (\d+)/i, 'darf maximal $1 Zeichen lang sein'],
      [/must be an integer number/i, 'muss eine ganze Zahl sein'],
      [/must be a number/i, 'muss eine Zahl sein'],
      [/must be a string/i, 'muss ein Text sein'],
      [/should not be empty/i, 'darf nicht leer sein'],
      [/must be a boolean/i, 'muss ein Wahrheitswert sein'],
    ]
    for (const [pattern, replacement] of patterns) {
      if (pattern.test(msg)) return msg.replace(pattern, replacement)
    }
    return msg
  }

  // Datenquelle für Projekt/Tätigkeit (Ref-Layer vs. Legacy) — für den Edit-Dialog.
  const projectIsRef = usesRefLayer(timeConfig?.projectSource ?? 'INTERNAL')
  const activityIsRef = usesRefLayer(timeConfig?.activityTypeSource ?? 'INTERNAL')

  // Standardfeld-Sichtbarkeit (wie Buchungsmaske / ClockPage)
  const isFieldVisible = (name: string) => standardFieldDefs.find((f) => f.name === name)?.visible ?? false
  const isFieldRequired = (name: string) => standardFieldDefs.find((f) => f.name === name)?.required ?? false

  // Eigene Datenquelle für den Edit-Dialog — kaskadiert über die gewählten
  // Projekt-/Positions-IDs (Ref-Modus), exakt wie die Buchungsmaske (ClockPage).
  const editBookingDS = useBookingDataSource({
    cascadePositions: true,
    selectedProjectRefId: editForm.projectRefId || null,
    selectedPositionRefId: editForm.selectedProjektposition || null,
  })
  // Effektive Listen — Ref- oder Legacy-Quelle je nach Modus.
  type ProjStepLike = { id: string; nummer: number | null; name: string }
  type ProjLike = { id: string; projectNumber?: string; name: string; externalId?: string | null }
  const editProjectsUI: ProjLike[] = projectIsRef ? editBookingDS.refProjects : projects
  const editActivitiesUI: { id: string; name: string }[] = activityIsRef ? editBookingDS.refActivities : activityTypes
  const editPositionsUI: ProjStepLike[] = projectIsRef ? editBookingDS.refPositions.map(p => ({ id: p.id, nummer: p.nummer, name: p.name })) : editProjektpositionen
  const editStepsUI: ProjStepLike[] = projectIsRef ? editBookingDS.refSteps.map(s => ({ id: s.id, nummer: s.nummer, name: s.name })) : editArbeitsschritte

  // Kaskade Edit (Legacy): Projektpositionen laden wenn Projekt gewählt.
  useEffect(() => {
    if (projectIsRef) return
    if (!editForm.projectId) { setEditProjektpositionen([]); return }
    timeConfigApi.getProjektpositionenByProject(editForm.projectId)
      .then(({ data }) => setEditProjektpositionen(data ?? []))
      .catch(() => setEditProjektpositionen([]))
  }, [editForm.projectId, projectIsRef])

  // Kaskade Edit (Legacy): Arbeitsschritte laden wenn Projektposition gewählt.
  useEffect(() => {
    if (projectIsRef) return
    if (!editForm.selectedProjektposition) { setEditArbeitsschritte([]); return }
    timeConfigApi.getArbeitsschritteByProjektposition(editForm.selectedProjektposition)
      .then(({ data }) => setEditArbeitsschritte(data ?? []))
      .catch(() => setEditArbeitsschritte([]))
  }, [editForm.selectedProjektposition, projectIsRef])

  // Legacy-Edit: Snapshot-Namen auf die geladenen Kaskaden-IDs zurückmappen.
  useEffect(() => {
    if (projectIsRef || editingEntryId === null) return
    if (editForm.selectedProjektposition || editPositionsUI.length === 0) return
    const ppName = editStandardFields['projektposition']
    if (!ppName) return
    const match = editPositionsUI.find((p) => p.name === ppName)
    if (match) setEditForm((p) => ({ ...p, selectedProjektposition: match.id }))
  }, [editPositionsUI, projectIsRef, editingEntryId, editForm.selectedProjektposition, editStandardFields])

  useEffect(() => {
    if (projectIsRef || editingEntryId === null) return
    if (editForm.selectedArbeitsschritt || editStepsUI.length === 0) return
    const asName = editStandardFields['arbeitsschritt']
    if (!asName) return
    const match = editStepsUI.find((s) => s.name === asName)
    if (match) setEditForm((p) => ({ ...p, selectedArbeitsschritt: match.id }))
  }, [editStepsUI, projectIsRef, editingEntryId, editForm.selectedArbeitsschritt, editStandardFields])

  const openEditDialog = (entry: TimeEntry) => {
    setReviewingEntryId(null)
    setEditingEntryId(entry.id)
    setEditError(null)
    setEditForm({
      date: entry.date.substring(0, 10),
      startTime: toTimeInput(entry.startTime),
      endTime: toTimeInput(entry.endTime),
      breakMinutes: entry.breakMinutes != null ? String(entry.breakMinutes) : '0',
      note: entry.note ?? '',
      projectId: entry.projectId ?? '',
      activityTypeId: entry.activityTypeId ?? '',
      projectRefId: (entry as unknown as { projectRefId?: string }).projectRefId ?? '',
      activityRefId: (entry as unknown as { activityRefId?: string }).activityRefId ?? '',
      // Im Ref-Modus stehen die Positions-/Step-IDs am Eintrag; im Legacy-Modus
      // werden sie nach dem Kaskaden-Laden per Namensabgleich nachgezogen.
      selectedProjektposition: (entry as unknown as { positionRefId?: string }).positionRefId ?? '',
      selectedArbeitsschritt: (entry as unknown as { stepRefId?: string }).stepRefId ?? '',
    })
    // Alle Standard-Felder (Snapshot) editierbar machen
    const sf = (entry.standardFields as Record<string, unknown> | null) ?? {}
    const sfStr: Record<string, string> = {}
    for (const [k, v] of Object.entries(sf)) sfStr[k] = v == null ? '' : String(v)
    setEditStandardFields(sfStr)
    // Alle Zusatzfelder (Typ A) editierbar machen
    const cf = (entry.customFields as Record<string, unknown> | null) ?? {}
    const cfStr: Record<string, string> = {}
    for (const [k, v] of Object.entries(cf)) cfStr[k] = v == null ? '' : String(v)
    setEditCustomFields(cfStr)
    // Read-only Snapshot-Infos (nicht editierbar): Preise, Qualifikation, GPS
    const ro: { label: string; value: string }[] = []
    const e = entry as unknown as Record<string, unknown>
    if (e['qualifikation']) ro.push({ label: t('timeTracking.qualifikation', { defaultValue: 'Qualifikation' }), value: String(e['qualifikation']) })
    if (e['selbstkostenstundensatz'] != null) ro.push({ label: t('timeTracking.selbstkostenstundensatz', { defaultValue: 'Selbstkosten/h' }), value: String(e['selbstkostenstundensatz']) })
    if (e['verkaufsstundensatz'] != null) ro.push({ label: t('timeTracking.verkaufsstundensatz', { defaultValue: 'Verkauf/h' }), value: String(e['verkaufsstundensatz']) })
    if (e['gpsStartLat'] != null && e['gpsStartLng'] != null) ro.push({ label: 'GPS Start', value: `${e['gpsStartLat']}, ${e['gpsStartLng']}` })
    if (e['gpsEndLat'] != null && e['gpsEndLng'] != null) ro.push({ label: 'GPS Ende', value: `${e['gpsEndLat']}, ${e['gpsEndLng']}` })
    setEditReadonly(ro)
  }

  const handleEditSubmit = async () => {
    if (!editingEntryId) return
    setEditLoading(true)
    setEditError(null)
    try {
      // Eingegebene Wall-Clock-Zeit 1:1 als UTC-Stempel kennzeichnen — KEIN
      // new Date(...).toISOString() (würde den lokalen TZ-Offset anwenden und
      // z.B. 13:30 in CEST fälschlich zu 11:30Z verschieben). Konsistent mit
      // clockIn/clockOut, manuellen Buchungen und der PWA.
      const buildISO = (time: string) =>
        time ? `${editForm.date}T${time}:00.000Z` : undefined

      // Standardfelder (Hierarchie-Snapshot) aus der kaskadierten Auswahl neu
      // aufbauen — analog zur Buchungsmaske (V1-Pfad). Freie (Nicht-Hierarchie-)
      // Felder inkl. Betriebsstätte behalten ihre editierten Werte.
      const hierarchyFieldNames = new Set(['projekt', 'projektnummer', 'idprojekt',
        'projektposition', 'idprojektposition', 'nummerprojektposition',
        'arbeitsschritt', 'idarbeitsschritt', 'taetigkeit'])
      const editSf: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(editStandardFields)) {
        if (hierarchyFieldNames.has(k)) continue
        if (v !== undefined && v !== null && v !== '') editSf[k] = v
      }
      const editProjObj = editProjectsUI.find(p => p.id === (projectIsRef ? editForm.projectRefId : editForm.projectId))
      if (editProjObj) {
        editSf['projekt'] = editProjObj.name
        if (editProjObj.projectNumber) editSf['projektnummer'] = editProjObj.projectNumber
        if (editProjObj.externalId) editSf['idprojekt'] = Number(editProjObj.externalId) || editProjObj.externalId
      }
      const editPpObj = editPositionsUI.find(pp => pp.id === editForm.selectedProjektposition)
      if (editPpObj) {
        editSf['projektposition'] = editPpObj.name
        if (editPpObj.nummer != null) editSf['nummerprojektposition'] = editPpObj.nummer
      }
      const editAsObj = editStepsUI.find(a => a.id === editForm.selectedArbeitsschritt)
      if (editAsObj) editSf['arbeitsschritt'] = editAsObj.name

      const payload: UpdateTimeEntryPayload = {
        date: editForm.date || undefined,
        startTime: buildISO(editForm.startTime),
        endTime: editForm.endTime ? buildISO(editForm.endTime) : undefined,
        breakMinutes: Number(editForm.breakMinutes) || 0,
        note: editForm.note || undefined,
        // Projekt/Position/Arbeitsschritt + Tätigkeit je nach Datenquelle routen
        // (exakt wie buildBookingIds in der Buchungsmaske).
        ...editBookingDS.buildBookingIds({
          legacyProjectId:      projectIsRef  ? null : editForm.projectId,
          legacyActivityTypeId: activityIsRef ? null : editForm.activityTypeId,
          refProjectId:         projectIsRef  ? editForm.projectRefId : null,
          refPositionId:        projectIsRef  ? editForm.selectedProjektposition : null,
          refStepId:            projectIsRef  ? editForm.selectedArbeitsschritt : null,
          refActivityId:        activityIsRef ? editForm.activityRefId : null,
        }),
        // Leere Auswahl explizit auf null setzen (buildBookingIds lässt leere IDs weg).
        ...(projectIsRef
          ? { projectRefId: editForm.projectRefId || null, positionRefId: editForm.selectedProjektposition || null, stepRefId: editForm.selectedArbeitsschritt || null }
          : { projectId: editForm.projectId || null }),
        ...(activityIsRef
          ? { activityRefId: editForm.activityRefId || null }
          : { activityTypeId: editForm.activityTypeId || null }),
        ...(Object.keys(editSf).length > 0 && { standardFields: editSf }),
        ...(Object.keys(editCustomFields).length > 0 && { customFields: editCustomFields }),
      }
      await timeEntriesApi.update(editingEntryId, payload)
      fetchEntries(filterValues, page, sortConfig)
      setEditingEntryId(null)
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message
      const msg = Array.isArray(raw) ? (raw as string[]).join(', ') : (raw as string | undefined)
      setEditError(translateApiError(msg))
    } finally {
      setEditLoading(false)
    }
  }

  // ── Review ────────────────────────────────────────────────────────────────────
  const handleReviewSubmit = async () => {
    if (!reviewingEntryId) return
    setReviewLoading(true)
    try {
      await timeEntriesApi.review(reviewingEntryId, { managerNote: reviewData.managerNote || undefined })
      fetchEntries(filterValues, page)
      setReviewingEntryId(null)
      setReviewData({ id: '', managerNote: '' })
    } catch (err) {
      console.error('Failed to review entry', err)
      setError(t('timeTracking.errorReviewing', { defaultValue: 'Prüfung konnte nicht gespeichert werden.' }))
    } finally {
      setReviewLoading(false)
    }
  }

  // ── Genehmigen (2. Stufe) / Zurücknehmen / Löschen ─────────────────────────────
  const handleApprove = async (entryId: string) => {
    setActionEntryId(entryId)
    try {
      await timeEntriesApi.approve(entryId)
      fetchEntries(filterValues, page, sortConfig)
    } catch (err) {
      console.error('Failed to approve entry', err)
      setError(t('timeTracking.errorApproving', { defaultValue: 'Genehmigung konnte nicht gespeichert werden.' }))
    } finally {
      setActionEntryId(null)
    }
  }

  const handleUnreview = async (entryId: string) => {
    setActionEntryId(entryId)
    try {
      await timeEntriesApi.unreview(entryId)
      fetchEntries(filterValues, page, sortConfig)
    } catch (err) {
      console.error('Failed to reset entry', err)
      setError(t('timeTracking.errorReviewing', { defaultValue: 'Aktion konnte nicht gespeichert werden.' }))
    } finally {
      setActionEntryId(null)
    }
  }

  const handleDelete = async (entry: TimeEntry) => {
    if (!(await bestaetige(t('timeTracking.confirmDelete', { defaultValue: 'Diesen Zeiteintrag wirklich löschen?' })))) return
    setActionEntryId(entry.id)
    try {
      await timeEntriesApi.delete(entry.id)
      fetchEntries(filterValues, page, sortConfig)
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { message?: unknown } } })?.response?.data?.message
      const msg = Array.isArray(raw) ? (raw as string[]).join(', ') : (raw as string | undefined)
      setError(msg || t('timeTracking.errorDeleting', { defaultValue: 'Eintrag konnte nicht gelöscht werden.' }))
    } finally {
      setActionEntryId(null)
    }
  }

  // Löschfreigabe: abhängig von der Bearbeitungs-/Löschrichtlinie des Mandanten
  const deletionEnabled = (() => {
    const p = timeConfig?.editPolicy
    return p === 'FREE' || p === 'TIME_WINDOW'
  })()

  // Abgeleitete Snapshot-Felder (kommen aus Projekt/Tätigkeit-Auswahl) — nicht als
  // eigene Textfelder editieren, um Doppeleingaben zu vermeiden.
  const STD_DERIVED = new Set([
    'projekt', 'projektnummer', 'idprojekt', 'taetigkeit',
    'idprojektposition', 'nummerprojektposition', 'idarbeitsschritt',
    'projektposition', 'arbeitsschritt', 'betriebsstaette',
  ])
  const editInputCls = 'w-full rounded-md border border-input bg-background px-3 py-2 text-foreground focus:outline-none focus:ring-2 focus:ring-ring'
  const editLabelCls = 'block text-sm font-medium text-foreground'

  const renderEditForm = () => (
    <div className="bg-muted/30 p-6">
      <h3 className="mb-4 font-semibold text-foreground">{t('timeTracking.editEntry', { defaultValue: 'Eintrag bearbeiten' })}</h3>
      {editError && <p className="mb-3 text-sm text-destructive">{editError}</p>}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.date', { defaultValue: 'Datum' })}</label>
          <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} className={editInputCls} />
        </div>
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.startTime', { defaultValue: 'Von' })}</label>
          <input type="time" value={editForm.startTime} onChange={(e) => setEditForm({ ...editForm, startTime: e.target.value })} className={editInputCls} />
        </div>
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.endTime', { defaultValue: 'Bis' })}</label>
          <input type="time" value={editForm.endTime} onChange={(e) => setEditForm({ ...editForm, endTime: e.target.value })} className={editInputCls} />
        </div>
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.break', { defaultValue: 'Pause (Min.)' })}</label>
          <input type="number" min="0" value={editForm.breakMinutes} onChange={(e) => setEditForm({ ...editForm, breakMinutes: e.target.value })} className={editInputCls} />
        </div>
        {/* Betriebsstätte – Dropdown wie Buchungsmaske */}
        {isFieldVisible('betriebsstaette') && (
          <div className="space-y-1.5">
            <label className={editLabelCls}>
              {standardFieldDefs.find(f => f.name === 'betriebsstaette')?.label ?? t('timeTracking.betriebsstaette', { defaultValue: 'Betriebsstätte' })}
              {isFieldRequired('betriebsstaette') && <span className="text-destructive"> *</span>}
            </label>
            <SearchSelect
              value={editStandardFields['betriebsstaette'] ?? ''}
              onChange={(v) => setEditStandardFields((prev) => ({ ...prev, betriebsstaette: v }))}
              options={betriebsstaetteItems.map((item) => ({ value: item.name, label: item.name }))}
              placeholder="– Betriebsstätte wählen –"
              className={editInputCls}
            />
          </div>
        )}
        {/* Projekt (Ref-Layer oder Legacy je nach Datenquelle) – suchbar wie Buchungsmaske */}
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.project', { defaultValue: 'Projekt' })}</label>
          <SearchSelect
            value={projectIsRef ? editForm.projectRefId : editForm.projectId}
            onChange={(v) => setEditForm({ ...editForm, [projectIsRef ? 'projectRefId' : 'projectId']: v, selectedProjektposition: '', selectedArbeitsschritt: '' })}
            options={editProjectsUI.map((p) => ({ value: p.id, label: `${p.projectNumber ? `${p.projectNumber} · ` : ''}${p.name}` }))}
            placeholder={t('timeTracking.noProject', { defaultValue: '— Kein Projekt —' })}
            className={editInputCls}
          />
        </div>
        {/* Projektposition – kaskadierendes Dropdown wie Buchungsmaske */}
        {(projectIsRef ? editForm.projectRefId : editForm.projectId) && (
          <div className="space-y-1.5">
            <label className={editLabelCls}>
              {t('timeTracking.projectPosition', { defaultValue: 'Projektposition' })}
              {isFieldRequired('projektposition') && <span className="text-destructive"> *</span>}
            </label>
            <SearchSelect
              value={editForm.selectedProjektposition}
              onChange={(v) => setEditForm({ ...editForm, selectedProjektposition: v, selectedArbeitsschritt: '' })}
              options={editPositionsUI.map((pp) => ({ value: pp.id, label: `${pp.nummer != null ? `${pp.nummer} – ` : ''}${pp.name}` }))}
              placeholder={t('timeTracking.projectPosition', { defaultValue: 'Projektposition wählen' })}
              className={editInputCls}
            />
          </div>
        )}
        {/* Arbeitsschritt – kaskadierendes Dropdown wie Buchungsmaske */}
        {editForm.selectedProjektposition && (
          <div className="space-y-1.5">
            <label className={editLabelCls}>
              {t('timeTracking.arbeitsschritt', { defaultValue: 'Arbeitsschritt' })}
              {isFieldRequired('arbeitsschritt') && <span className="text-destructive"> *</span>}
            </label>
            <SearchSelect
              value={editForm.selectedArbeitsschritt}
              onChange={(v) => setEditForm({ ...editForm, selectedArbeitsschritt: v })}
              options={editStepsUI.map((as) => ({ value: as.id, label: as.name }))}
              placeholder={t('timeTracking.arbeitsschritt', { defaultValue: 'Arbeitsschritt wählen' })}
              className={editInputCls}
            />
          </div>
        )}
        {/* Tätigkeit – suchbar wie Buchungsmaske */}
        <div className="space-y-1.5">
          <label className={editLabelCls}>{t('timeTracking.activityType', { defaultValue: 'Tätigkeit' })}</label>
          <SearchSelect
            value={activityIsRef ? editForm.activityRefId : editForm.activityTypeId}
            onChange={(v) => setEditForm({ ...editForm, [activityIsRef ? 'activityRefId' : 'activityTypeId']: v })}
            options={editActivitiesUI.map((a) => ({ value: a.id, label: a.name }))}
            placeholder={t('timeTracking.noActivity', { defaultValue: '— Keine Tätigkeit —' })}
            className={editInputCls}
          />
        </div>
        {/* Übrige vorhandene Standard-Felder (ohne abgeleitete) – echtes Label aus den Felddefinitionen */}
        {Object.keys(editStandardFields).filter((k) => !STD_DERIVED.has(k)).map((key) => {
          const def = standardFieldDefs.find((f) => f.name === key)
          return (
            <div key={`sf-${key}`} className="space-y-1.5">
              <label className={editLabelCls}>{def?.label ?? key}</label>
              <input type={def?.type === 'number' ? 'number' : 'text'} value={editStandardFields[key]}
                onChange={(e) => setEditStandardFields((prev) => ({ ...prev, [key]: e.target.value }))}
                className={editInputCls} />
            </div>
          )
        })}
        {/* Zusatzfelder (Typ A) – aus dem Schema, immer sichtbar */}
        {(timeConfig?.timeEntryCustomFieldsSchema ?? []).map((f) => (
          <div key={`cf-${f.name}`} className="space-y-1.5">
            <label className={editLabelCls}>{f.label}</label>
            <input type={f.type === 'number' ? 'number' : 'text'} value={editCustomFields[f.name] ?? ''}
              onChange={(e) => setEditCustomFields((prev) => ({ ...prev, [f.name]: e.target.value }))}
              className={editInputCls} />
          </div>
        ))}
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-3">
          <label className={editLabelCls}>{t('timeTracking.note', { defaultValue: 'Notiz' })}</label>
          <input type="text" value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
            placeholder={t('timeTracking.notePlaceholder', { defaultValue: 'Optional...' })}
            className={editInputCls} />
        </div>
      </div>
      {editReadonly.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 rounded-md border border-border bg-background/50 px-3 py-2 text-xs text-muted-foreground">
          {editReadonly.map((r) => (
            <span key={r.label}><span className="font-medium text-foreground">{r.label}:</span> {r.value}</span>
          ))}
        </div>
      )}
      <div className="mt-4 flex gap-3">
        <Button onClick={handleEditSubmit} disabled={editLoading} className="bg-primary text-primary-foreground hover:bg-primary/90">
          {editLoading ? t('common.saving', { defaultValue: 'Wird gespeichert...' }) : t('common.apply', { defaultValue: 'Übernehmen' })}
        </Button>
        <Button onClick={() => { setEditingEntryId(null); setEditError(null) }} disabled={editLoading} variant="outline" className="border-border text-foreground hover:bg-card">
          {t('common.cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      </div>
    </div>
  )

  const renderReviewForm = () => (
    <div className="bg-muted/30 p-6">
      <h3 className="mb-4 font-semibold text-foreground">{t('timeTracking.reviewEntry', { defaultValue: 'Eintrag prüfen' })}</h3>
      <div className="space-y-4">
        <div className="space-y-2">
          <label htmlFor="reviewNote" className={editLabelCls}>{t('timeTracking.managerNote', { defaultValue: 'Notiz des Managers' })}</label>
          <textarea id="reviewNote" value={reviewData.managerNote} onChange={(e) => setReviewData({ ...reviewData, managerNote: e.target.value })} placeholder={t('timeTracking.notePlaceholder', { defaultValue: 'Optional note...' })} className={editInputCls} rows={3} />
        </div>
        <div className="flex gap-3">
          <Button onClick={handleReviewSubmit} disabled={reviewLoading} className="bg-primary text-primary-foreground hover:bg-primary/90">
            {reviewLoading ? t('common.saving', { defaultValue: 'Wird gespeichert...' }) : t('timeTracking.markReviewed', { defaultValue: 'Als geprüft markieren' })}
          </Button>
          <Button onClick={() => { setReviewingEntryId(null); setReviewData({ id: '', managerNote: '' }) }} disabled={reviewLoading} variant="outline" className="border-border text-foreground hover:bg-card">
            {t('common.cancel', { defaultValue: 'Abbrechen' })}
          </Button>
        </div>
      </div>
    </div>
  )

  // ── Clear Suspicion (Phase 9) ─────────────────────────────────────────────────
  const handleClearSuspicion = async (entryId: string) => {
    setClearingEntryId(entryId)
    try {
      await timeEntriesApi.clearSuspicion(entryId)
      fetchEntries(filterValues, page, sortConfig)
    } catch (err) {
      console.error('Failed to clear suspicion', err)
      setError(t('timeTracking.errorClearingSuspicion', { defaultValue: 'Verdacht konnte nicht entfernt werden.' }))
    } finally {
      setClearingEntryId(null)
    }
  }

  if (!user) {
    return <div className="flex items-center justify-center py-8"><p className="text-muted-foreground">{t('common.loading')}</p></div>
  }

  return (
    <div className={pageWide}>
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">{t('timeTracking.summary', { defaultValue: 'Auswertung' })}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('timeTracking.summaryDescription', { defaultValue: 'Zeiteinträge überprüfen und auswerten' })}</p>
        </div>
        {canExport && (
          <ExportButton
            request={handleExport}
            defaultFilename="zeiterfassung"
            label={t('timeTracking.export', { defaultValue: 'Exportieren' })}
            mail={{
              source: 'EXPORT_TIME_ENTRIES',
              title: t('mailExport.titles.EXPORT_TIME_ENTRIES'),
              subject: betreffMitZeitraum(t('mailExport.titles.EXPORT_TIME_ENTRIES'), filterValues['dateFrom']?.value, filterValues['dateTo']?.value),
            }}
          />
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}

      {/* FilterBar */}
      <FilterBar
        availableFields={ohneFehlendeRechte(
          buildAvailableFilterFields(t, terms, filterProjects, filterActivities, timeConfig, canViewAll, users, filterPositions, filterSteps),
          (p) => erfuelltRecht(p, user?.permissions),
        )}
        value={filterValues}
        onChange={handleFiltersChange}
        layoutKey="summary"
        defaultFieldKeys={DEFAULT_FILTER_FIELD_KEYS}
        adresse={adresse}
      />

      {/* Table card */}
      <div className="relative rounded-lg border border-border bg-card overflow-hidden">

        {/* Gear icon – top-right of the table card */}
        <button
          type="button"
          onClick={() => setColumnConfigMode((v) => !v)}
          className={`absolute right-1.5 top-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-md transition-colors ${
            columnConfigMode
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:bg-muted hover:text-foreground'
          }`}
          title={columnConfigMode ? t('columnBar.done', { defaultValue: 'Fertig' }) : t('columnBar.configure', { defaultValue: 'Spalten anpassen' })}
        >
          {columnConfigMode ? <Check className="h-4 w-4" /> : <Settings className="h-4 w-4" />}
        </button>

        {/* Hilfe „?" – links neben dem Spalten-Zahnrad */}
        <HelpPopover topic="columns" className="absolute right-8 top-1.5" hilfe={HILFE_VERWEISE.zusammenfassung} />

        {/* AP-23 Phase D: Zurücksetzen / als Mandanten-Standard speichern */}
        {columnConfigMode && (
          <div className="border-b border-border bg-muted/30 px-3 py-2 pr-16">
            <LayoutDefaultActions
              settingKey={COLUMN_LAYOUT_KEY}
              currentValue={columnLayout}
                    onReset={(value) => {
                const next = (value as ColumnLayout | null) ?? {
                  columnKeys: DEFAULT_COLUMN_KEYS, columnWidths: {}, sortConfig: null,
                }
                markUiSettingApplied(COLUMN_LAYOUT_KEY, next)
                setColumnLayout(next)
              }}
            />
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/50">
              <tr>
                {/* Fixed first column: Date – sortable + resizable */}
                <th
                  className="relative px-3 py-1 text-left font-medium text-foreground"
                  style={columnWidths['date'] ? { width: columnWidths['date'], minWidth: columnWidths['date'] } : undefined}
                >
                  <button
                    type="button"
                    onClick={() => handleSort('date')}
                    className="flex items-center gap-1 hover:text-primary"
                  >
                    {t('timeTracking.date', { defaultValue: 'Datum' })}
                    {sortConfig?.key === 'date'
                      ? sortConfig.dir === 'asc'
                        ? <ArrowUp className="h-3 w-3 text-primary" />
                        : <ArrowDown className="h-3 w-3 text-primary" />
                      : <ArrowUpDown className="h-3 w-3 opacity-30" />
                    }
                  </button>
                  <div
                    className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                    onMouseDown={(e) => handleResizeStart(e, 'date')}
                  />
                </th>

                {/* Configurable columns */}
                {shownColumns.map((col) => {
                  const isSorted = sortConfig?.key === col.key
                  const isSortable = SERVER_SORTABLE.has(col.key) && !columnConfigMode
                  const isDragOver = dragOverKey === col.key
                  const colWidth = columnWidths[col.key]
                  return (
                    <th
                      key={col.key}
                      style={colWidth ? { width: colWidth, minWidth: colWidth } : undefined}
                      className={`relative select-none px-3 py-1 font-medium text-foreground
                        ${col.align === 'right' ? 'text-right' : 'text-left'}
                        ${columnConfigMode ? 'cursor-grab' : ''}
                        ${isDragOver ? 'bg-primary/10 outline outline-2 outline-primary' : ''}
                      `}
                      draggable={columnConfigMode}
                      onDragStart={columnConfigMode ? () => handleDragStart(col.key) : undefined}
                      onDragOver={columnConfigMode ? (e) => handleDragOver(e, col.key) : undefined}
                      onDrop={columnConfigMode ? () => handleDrop(col.key) : undefined}
                      onDragEnd={columnConfigMode ? handleDragEnd : undefined}
                    >
                      <div className={`flex items-center gap-1 ${col.align === 'right' ? 'justify-end' : ''}`}>
                        {/* Sort indicator / clickable label */}
                        {isSortable ? (
                          <button
                            type="button"
                            onClick={() => handleSort(col.key)}
                            className="flex items-center gap-1 hover:text-primary"
                          >
                            {col.label}
                            {isSorted
                              ? sortConfig!.dir === 'asc'
                                ? <ArrowUp className="h-3 w-3 text-primary" />
                                : <ArrowDown className="h-3 w-3 text-primary" />
                              : <ArrowUpDown className="h-3 w-3 opacity-30" />
                            }
                          </button>
                        ) : (
                          <span>{col.label}</span>
                        )}
                        {/* Remove button in config mode */}
                        {columnConfigMode && (
                          <button
                            type="button"
                            onClick={() => handleRemoveColumn(col.key)}
                            className="ml-1 rounded p-0.5 text-destructive hover:text-destructive/80 text-xs"
                          >✕</button>
                        )}
                      </div>
                      {/* Resize handle */}
                      <div
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-primary/40 active:bg-primary"
                        onMouseDown={(e) => handleResizeStart(e, col.key)}
                      />
                    </th>
                  )
                })}

                {/* Config mode: add column button in header area */}
                {columnConfigMode && availableToAddColumns.length > 0 && (
                  <th className="px-2 py-1.5">
                    <div className="relative" ref={addColRef}>
                      <button
                        type="button"
                        onClick={() => { const r = addColRef.current?.getBoundingClientRect(); if (r) setAddColPos({ top: r.bottom + 4, left: r.left }); setAddColDropdownOpen((v) => !v) }}
                        className="whitespace-nowrap rounded border border-dashed border-border px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
                      >
                        + {t('columnBar.addColumn', { defaultValue: 'Spalte' })}
                      </button>
                      {addColDropdownOpen && (
                        <div className="fixed z-50 max-h-64 min-w-[200px] overflow-y-auto rounded-md border border-border bg-background shadow-lg" style={{ top: addColPos.top, left: addColPos.left }}>
                          {Object.entries(groupedAvailableColumns).map(([cat, cols]) => (
                            <div key={cat}>
                              <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                {COLUMN_CATEGORY_LABELS[cat] ?? cat}
                              </div>
                              {cols.map((c) => (
                                <button key={c.key} type="button" onClick={() => handleAddColumn(c.key)} className="flex w-full items-center px-4 py-2 text-sm text-foreground hover:bg-muted">
                                  {c.label}
                                </button>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </th>
                )}

                {/* Fixed last column: Actions */}
                <th className="px-3 py-1.5 text-left font-medium text-foreground">
                  {t('timeTracking.actions', { defaultValue: 'Aktionen' })}
                </th>
              </tr>
            </thead>

            <tbody>
              {loading && entries.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              )}
              {!loading && entries.length === 0 && (
                <tr><td colSpan={totalColCount} className="px-6 py-8 text-center text-muted-foreground">{t('timeTracking.noEntries', { defaultValue: 'Keine Einträge gefunden' })}</td></tr>
              )}

              {entries.map((entry, idx) => (
                <Fragment key={entry.id}>
                <tr className={`border-b border-border last:border-0 ${idx % 2 === 0 ? 'bg-background' : 'bg-muted/20'} hover:bg-accent/30 transition-colors ${(editingEntryId === entry.id || reviewingEntryId === entry.id) ? 'bg-accent/40' : ''}`}>
                  {/* Date – always first */}
                  <td className="px-3 py-1 tabular-nums whitespace-nowrap text-muted-foreground">{formatDate(entry.date)}</td>

                  {/* Configurable columns */}
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-1 whitespace-nowrap ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {renderCell(entry, col)}
                    </td>
                  ))}

                  {/* Spacer cell when config mode adds a column header cell */}
                  {columnConfigMode && availableToAddColumns.length > 0 && <td />}

                  {/* Actions – always last */}
                  <td className="px-3 py-0 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 flex-nowrap">
                      {isManager && entry.status !== 'SYNCED' && (
                        <Button
                          onClick={() => openEditDialog(entry)}
                          size="sm" variant="outline" className="border-border text-foreground hover:bg-card h-7 w-7 p-0"
                          title={t('timeTracking.editEntry', { defaultValue: 'Eintrag bearbeiten' })}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isManager && entry.status === 'SAVED' && (
                        <Button
                          onClick={() => { setEditingEntryId(null); setReviewingEntryId(entry.id); setReviewData({ id: entry.id, managerNote: entry.managerNote || '' }) }}
                          size="sm" variant="outline" className="border-border text-foreground hover:bg-card h-7 w-7 p-0"
                          title={t('timeTracking.review', { defaultValue: 'Prüfen' })}
                        >
                          <ClipboardCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isManager && entry.status === 'REVIEWED' && (
                        <Button
                          onClick={() => handleApprove(entry.id)}
                          disabled={actionEntryId === entry.id}
                          size="sm" variant="outline"
                          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900/20 h-7 w-7 p-0"
                          title={t('timeTracking.approve', { defaultValue: 'Genehmigen' })}
                        >
                          <BadgeCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isManager && (entry.status === 'REVIEWED' || entry.status === 'APPROVED') && (
                        <Button
                          onClick={() => handleUnreview(entry.id)}
                          disabled={actionEntryId === entry.id}
                          size="sm" variant="outline" className="border-border text-muted-foreground hover:bg-card h-7 w-7 p-0"
                          title={t('timeTracking.unreview', { defaultValue: 'Prüfung/Genehmigung zurücknehmen' })}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Phase 9: Verdacht-Clear-Aktion */}
                      {isManager && Array.isArray(entry.suspicionFlags) && entry.suspicionFlags.length > 0 && !entry.suspicionCleared && (
                        <Button
                          onClick={() => handleClearSuspicion(entry.id)}
                          disabled={clearingEntryId === entry.id}
                          size="sm" variant="outline"
                          className="border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700 dark:text-amber-400 dark:hover:bg-amber-900/20 h-7 w-7 p-0"
                          title={t('timeTracking.clearSuspicionHint', { defaultValue: 'Als unbedenklich markieren' })}
                        >
                          <ShieldCheck className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {/* Löschen – nur wenn der Admin die Löschfreigabe erteilt hat (Bearbeitungsrichtlinie) */}
                      {isManager && deletionEnabled && entry.status !== 'SYNCED' && (
                        <Button
                          onClick={() => handleDelete(entry)}
                          disabled={actionEntryId === entry.id}
                          size="sm" variant="outline"
                          className="border-destructive/40 text-destructive hover:bg-destructive/10 h-7 w-7 p-0"
                          title={t('common.delete', { defaultValue: 'Löschen' })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isManager && (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </div>
                  </td>
                </tr>
                {/* Bearbeiten-/Prüfen-Formular direkt unter der Zeile */}
                {editingEntryId === entry.id && (
                  <tr>
                    <td colSpan={totalColCount} className="border-b border-border p-0">{renderEditForm()}</td>
                  </tr>
                )}
                {reviewingEntryId === entry.id && (
                  <tr>
                    <td colSpan={totalColCount} className="border-b border-border p-0">{renderReviewForm()}</td>
                  </tr>
                )}
                </Fragment>
              ))}

              {/* Totals row */}
              {entries.length > 0 && pagination.page >= pagination.totalPages && (
                <tr className="border-t-2 border-border bg-muted/50 font-medium">
                  <td className="px-3 py-1 text-muted-foreground whitespace-nowrap">
                    {loading ? 'Laden…' : `Summe (${pagination.total} Einträge)`}
                  </td>
                  {shownColumns.map((col) => (
                    <td key={col.key} className={`px-3 py-1 ${col.align === 'right' ? 'text-right tabular-nums' : ''}`}>
                      {totalsMinutes[col.key] != null ? (
                        <div className="flex flex-col items-end leading-tight">
                          <span>{formatDuration(totalsMinutes[col.key])}</span>
                          <span className="text-[11px] font-normal text-muted-foreground">{(totalsMinutes[col.key] / 60).toFixed(2)} h</span>
                        </div>
                      ) : (totals[col.key] ?? '')}
                    </td>
                  ))}
                  {columnConfigMode && availableToAddColumns.length > 0 && <td />}
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Bearbeiten/Prüfen werden inline als aufklappende Zeile unter dem jeweiligen
            Datensatz angezeigt — siehe renderEditForm()/renderReviewForm() im Tabellen-Body. */}
      </div>

      {/* Seitengröße */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <label htmlFor="summary-pagesize">Zeilen pro Seite</label>
        <input id="summary-pagesize" type="number" min={1} list="summary-pagesize-options" value={pageSizeInput} onChange={(e) => handlePageSizeInput(e.target.value)} className="w-24 rounded-md border border-input bg-background px-2 py-1 text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
        <datalist id="summary-pagesize-options"><option value="50" /><option value="100" /><option value="200" /></datalist>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t('common.pagingInfo', { defaultValue: 'Seite {{page}} von {{total}}', page: pagination.page, total: pagination.totalPages })}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1 || loading} variant="outline" className="border-border text-foreground hover:bg-card">
              {t('common.previous', { defaultValue: 'Zurück' })}
            </Button>
            {Array.from({ length: pagination.totalPages }).map((_, i) => (
              <Button key={i + 1} onClick={() => handlePageChange(i + 1)} disabled={loading} variant={pagination.page === i + 1 ? 'default' : 'outline'} className={pagination.page === i + 1 ? 'bg-primary text-primary-foreground' : 'border-border text-foreground hover:bg-card'}>
                {i + 1}
              </Button>
            ))}
            <Button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.totalPages || loading} variant="outline" className="border-border text-foreground hover:bg-card">
              {t('common.next', { defaultValue: 'Weiter' })}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
